import Button from "src/components/button/Button";
import LabelledCheck from "src/components/labelledcheck/LabelledCheck";
import SelectorWrapper from 'src/components/selectorwrapper/SelectorWrapper';
import theme from "src/components/themes/DefaultTheme";
import { StreamingProvider } from 'src/lib/movieinfo/movieinfotypes';
import { type ActionType, type IStreamingGenre, type IStreamingReleasesFilterState, STREAMING_GENRES } from 'src/lib/streaming/StreamingReleasesFilter';
import styled from 'src/lib/styles/css';
import { uniqBy } from "lodash";
import { useState } from "react";

//#region [Props]
type FilterProps = {
	streamingProviders: StreamingProvider[];
	filterDispatch: React.Dispatch<ActionType>;
	filterState: IStreamingReleasesFilterState;
};
//#endregion

//#region [Component]
export default function Filter({ streamingProviders, filterDispatch, filterState }: FilterProps) {
	const [selectedGenres, setSelectedGenres] = useState(filterState.selectedGenres);
	const [selectedProviders, setSelectedProviders] = useState(filterState.selectedProviders);

	return <SelectorWrapper onClose={() => filterDispatch({ type: "hide" })} heading="Valg">
		<SScrollContainer>
			<SFilterBLock>
				<SFilterBlockTitle>Sjangere</SFilterBlockTitle>
				<SLabelledCheck selected={selectedGenres.length === 0}
					selectionCallback={() => setSelectedGenres([])}
					value="Alle" />
				{STREAMING_GENRES.map(genre => <SLabelledCheck key={genre.id}
					value={genre.id}
					label={genre.name}
					selected={selectedGenres.includes(genre)}
					selectionCallback={(isSelected) => selectGenre(setSelectedGenres, selectedGenres, isSelected, genre)}
				/>)}
			</SFilterBLock>

			{(streamingProviders?.length ?? 0) > 0 && <SFilterBLock>
				<SFilterBlockTitle>Streamingtjenester</SFilterBlockTitle>
				<SLabelledCheck selected={selectedProviders.length === 0}
					selectionCallback={() => setSelectedProviders([])}
					value="Alle" />
				{streamingProviders.map(provider => <SLabelledCheck key={provider.id}
					selected={selectedProviders.includes(provider)}
					value={provider.id}
					label={provider.name!}
					selectionCallback={(isSelected) => selectProvider(setSelectedProviders, selectedProviders, isSelected, provider)}
				/>)}

			</SFilterBLock>}
		</SScrollContainer>
		<SControls>
			<Button text="Vis" onClick={() => filterDispatch({ type: "applyFilter", selectedGenres, selectedProviders })} />
			<Button text="Avbryt" onClick={() => filterDispatch({ type: "hide" })} />
		</SControls>
	</SelectorWrapper>
}
//#endregion

//#region [Other]
function selectGenre(
	setSelectedGenres: (providers: IStreamingGenre[]) => void,
	currGenres: IStreamingGenre[],
	isSelected: boolean,
	value: IStreamingGenre
) {
	if (isSelected) {
		setSelectedGenres(uniqBy([...currGenres, value], g => g.id));
	} else {
		setSelectedGenres(currGenres.filter(g => g !== value));
	}
}

function selectProvider(
	setSelectedProviders: (providers: StreamingProvider[]) => void,
	currProviders: StreamingProvider[],
	isSelected: boolean,
	value: StreamingProvider
) {
	if (isSelected) {
		setSelectedProviders(uniqBy([...currProviders, value], p => p.id));
	} else {
		setSelectedProviders(currProviders.filter(p => p.id !== value.id));
	}
}
//#endregion

//#region [Styles]
const SScrollContainer = styled.div`
	overflow-y: auto;
	max-height: 60vh;
	@media (min-width: 550px) {
		max-height: 420px;
	}
`;

const SFilterBLock = styled.div`
	display: flex;
	flex-wrap: wrap;
	padding: 10px;
	justify-content: flex-start;
`;

const SFilterBlockTitle = styled.h3`
	font-size: 16px;
	flex: 0 0 100%;
`;

const SLabelledCheck = styled(LabelledCheck)`
	flex: 0 1 auto;

`;

const SControls = styled.div`
	margin-top: 10px;
	display: flex;
	justify-content: center;
	gap: 10px;
`;
//#endregion